import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GeoJSONSource, Map as MlMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  clockTime,
  ALTERATION_NOTES,
  applyAlterationOpacity,
  applyLayerVisibility,
  assignmentPaint,
  ATTRIBUTION,
  claimPaint,
  DEFAULT_PROPERTY,
  type Evidence,
  evidencePaint,
  type FieldAssignment,
  LAYER_GROUPS,
  LAYERS,
  PLACEHOLDER_BOUNDS,
  positionPaint,
  propertyById,
} from '@kilroy/shared';

import { db } from '../db/db';
import { tilePack } from '../media/opfs';
import { useGps } from '../media/gps';
import { buildStyle, maplibregl } from '@kilroy/shared/map';
import { Sheet } from '../components/Sheet';
import { IconFix } from '../components/icons';
import { describeEvidence } from '../components/EvidenceRow';
import './map.css';

/* The map.
 *
 * Terrain and contours are the basemap — see map-style.ts for why. Everything
 * degrades rather than erroring: no signal means no roads, but the hillshade
 * keeps drawing from cached DEM tiles, the contours keep being computed from
 * them, and your own evidence is local anyway. There is no state in which this
 * screen shows a grey void and an error.
 */

const point = (e: Evidence) => e.geometry.type === 'Point';

const evidenceFC = (rows: Evidence[]) => ({
  type: 'FeatureCollection' as const,
  features: rows
    .filter((e) => point(e) && e.gps_accuracy_m >= 0)
    .map((e) => ({
      type: 'Feature' as const,
      id: e.id,
      geometry: e.geometry,
      properties: {
        id: e.id,
        type: e.evidence_type,
        confirmation: e.current_confirmation_status,
        sync: e.sync_state,
      },
    })),
});

const assignmentFC = (rows: FieldAssignment[]) => ({
  type: 'FeatureCollection' as const,
  features: rows
    .filter((a) => a.geometry.type === 'Polygon')
    .map((a) => ({
      type: 'Feature' as const,
      id: a.id,
      geometry: a.geometry,
      properties: { id: a.id, title: a.title },
    })),
});

export function MapScreen() {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const navigate = useNavigate();

  const [ready, setReady] = useState(false);
  const [pack, setPack] = useState<'checking' | 'present' | 'absent'>('checking');
  const [layersOpen, setLayersOpen] = useState(false);
  const [altOpacity, setAltOpacity] = useState(0.45);
  const [on, setOn] = useState<Record<string, boolean>>(
    Object.fromEntries(LAYERS.map((l) => [l.id, l.defaultOn])),
  );
  const [selected, setSelected] = useState<Evidence | null>(null);

  const evidence = useLiveQuery(() => db.evidence.toArray(), [], [] as Evidence[]);
  const assignments = useLiveQuery(() => db.assignments.toArray(), [], []);
  const property = propertyById(DEFAULT_PROPERTY);
  const gps = useGps(true);

  /* ── init ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!container.current || map.current) return;

    const m = new maplibregl.Map({
      container: container.current,
      center: property.center,
      zoom: 13,
      // A geologist tilts a map to understand a slope, then can't find north.
      // Pitch stays off; rotation stays on, because you do orient a paper map.
      pitchWithRotate: false,
      dragRotate: true,
      attributionControl: false,
      style: buildStyle(navigator.onLine),
    });

    /* Attribution is rendered by us, below — MapLibre's own control fights
       the type system and refuses to stay collapsed. The credit is legally
       required; a three-line caption over the terrain is not. */

    m.on('load', () => {
      m.addSource('evidence', { type: 'geojson', data: evidenceFC([]) });
      m.addSource('assignments', { type: 'geojson', data: assignmentFC([]) });
      m.addSource('position', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      /* `as never` on the paint objects only. map-style.ts is deliberately
         framework-free — it is imported by shared, which must not depend on
         maplibre-gl — so the paint specs are plain data there and are asserted
         here, at the one boundary where MapLibre's types apply. */
      const paint = <T,>(p: T) => p as never;

      m.addLayer({
        id: 'assignments-fill',
        type: 'fill',
        source: 'assignments',
        paint: paint(assignmentPaint.fill),
      });
      m.addLayer({
        id: 'assignments-line',
        type: 'line',
        source: 'assignments',
        paint: paint(assignmentPaint.line),
      });
      m.addLayer({ id: 'claims-line', type: 'line', source: 'assignments', paint: paint(claimPaint) });
      m.addLayer({ id: 'position-ring', type: 'circle', source: 'position', paint: paint(positionPaint.ring) });
      m.addLayer({ id: 'evidence', type: 'circle', source: 'evidence', paint: paint(evidencePaint) });
      m.addLayer({ id: 'position-dot', type: 'circle', source: 'position', paint: paint(positionPaint.dot) });

      // A 9px dot is a small target for a gloved thumb, so the hit test runs
      // against a padded box rather than the rendered radius.
      m.on('click', (e) => {
        const hits = m.queryRenderedFeatures(
          [
            [e.point.x - 14, e.point.y - 14],
            [e.point.x + 14, e.point.y + 14],
          ],
          { layers: ['evidence'] },
        );
        const id = hits[0]?.properties?.id as string | undefined;
        setSelected(id ? (evidenceRef.current.find((x) => x.id === id) ?? null) : null);
      });

      setReady(true);
    });

    map.current = m;
    void tilePack(property.id).then((f) => setPack(f ? 'present' : 'absent'));

    return () => {
      m.remove();
      map.current = null;
    };
  }, [property]);

  // The click handler closes over evidence once; a ref keeps it current.
  const evidenceRef = useRef<Evidence[]>([]);
  evidenceRef.current = evidence;

  useEffect(() => {
    if (!ready || !map.current) return;
    (map.current.getSource('evidence') as GeoJSONSource | undefined)?.setData(evidenceFC(evidence));
    (map.current.getSource('assignments') as GeoJSONSource | undefined)?.setData(
      assignmentFC(assignments),
    );
  }, [evidence, assignments, ready]);

  /* Own position, with the accuracy ring drawn to scale in metres. */
  useEffect(() => {
    if (!ready || !map.current || !gps.fix) return;
    (map.current.getSource('position') as GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [gps.fix.lon, gps.fix.lat] },
          properties: { accuracy: gps.fix.accuracy },
        },
      ],
    });
  }, [gps.fix, ready]);

  useEffect(() => {
    if (!ready || !map.current) return;
    applyLayerVisibility(map.current, on);
  }, [on, ready]);

  useEffect(() => {
    if (!ready || !map.current) return;
    applyAlterationOpacity(map.current, altOpacity);
  }, [altOpacity, ready]);

  const locate = () => {
    if (gps.fix) map.current?.flyTo({ center: [gps.fix.lon, gps.fix.lat], zoom: 16 });
  };

  return (
    <div className="map">
      <div ref={container} className="map__canvas" />

      <div className="map__chips">
        <button type="button" className="mapchip" onClick={() => setLayersOpen(true)}>
          Layers
        </button>
        <span className={`mapchip mapchip--${pack}`}>
          {property.name} · {pack === 'present' ? 'offline ready' : pack === 'checking' ? '…' : 'online'}
        </span>
      </div>

      <button
        type="button"
        className="map__locate"
        onClick={locate}
        disabled={!gps.fix}
        aria-label="Centre on my position"
      >
        <IconFix size={22} />
      </button>

      {selected && (
        <button
          type="button"
          className="map__card"
          onClick={() => navigate(`/evidence/${selected.id}`)}
        >
          <span className="map__card-title">{describeEvidence(selected)}</span>
          <span className="mono map__card-meta">
            {clockTime(selected.captured_at)}{' '}
            · ±{Math.round(selected.gps_accuracy_m)} m · open record →
          </span>
        </button>
      )}

      <p className="map__attrib mono">{ATTRIBUTION}</p>

      {layersOpen && (
        <Sheet title="Layers" onClose={() => setLayersOpen(false)}>
          {LAYER_GROUPS.map(({ group, layers }) => (
            <div key={group ?? 'base'} className="layergroup">
              {group && <div className="label layergroup__h">{group}</div>}
              <div className="stack" style={{ gap: 'var(--s-2)' }}>
                {layers.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    className="layerrow"
                    aria-pressed={on[l.id]}
                    onClick={() => setOn((s) => ({ ...s, [l.id]: !s[l.id] }))}
                  >
                    {l.swatch && (
                      <span
                        className="layerrow__swatch"
                        style={{ background: l.swatch }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="grow">
                      {l.label}
                      {l.note && <span className="layerrow__note">{l.note}</span>}
                    </span>
                    <span className="layerrow__box" aria-hidden="true" />
                  </button>
                ))}
              </div>

              {/* The published-survey group carries its caveats with it. A
                  geologist who turns these on is entitled to know what they
                  are before reading anything off them, not after. */}
              {group === 'Published alteration' && (
                <div className="altnote">
                  <label className="altnote__op">
                    <span className="grow">Opacity</span>
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={altOpacity}
                      onChange={(e) => setAltOpacity(Number(e.target.value))}
                    />
                  </label>
                  <p className="notice">{ALTERATION_NOTES.coverage}</p>
                  <p className="field__hint">{ALTERATION_NOTES.facies}</p>
                  <p className="field__hint">{ALTERATION_NOTES.commission}</p>
                  <p className="field__hint">
                    {ALTERATION_NOTES.source} · {ALTERATION_NOTES.colour}
                  </p>
                </div>
              )}
            </div>
          ))}

          <p className="field__hint" style={{ marginTop: 'var(--s-4)' }}>
            Terrain and contours are computed from elevation tiles on this phone, so they
            keep working once you lose signal. Roads, labels and the published alteration
            survey need a connection.
            {PLACEHOLDER_BOUNDS && ' Claim boundaries are placeholders until the real extents arrive.'}
          </p>
        </Sheet>
      )}
    </div>
  );
}
